import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiPhone, FiChevronDown, FiChevronUp, FiEye, FiDownload, FiPrinter, FiExternalLink, FiCopy, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import './ManageOrders.css';

const statusFlow = ['confirmed', 'delivered'];

const statusColors = {
  placed:     '#FFB347',
  confirmed:  '#6C63FF',
  delivered:  '#00D9A6',
  cancelled:  '#FF6B6B',
};

const COLOR_LABEL     = { bw: 'B&W', colour: 'Colour' };
const SIDE_LABEL      = { single: 'One side', both: 'Both sides' };
const ORIENT_LABEL    = { portrait: 'Portrait', landscape: 'Landscape' };

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const getProxyUrl = (url) => {
  if (!url) return '';
  if (url.includes('res.cloudinary.com')) {
    if (url.includes('/upload/proxy')) return url;
    const base = process.env.REACT_APP_API_URL || '/api';
    return `${base}/upload/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const getDownloadUrl = (url, filename) => {
  if (!url) return '';
  if (url.includes('res.cloudinary.com')) {
    const base = process.env.REACT_APP_API_URL || '/api';
    return `${base}/upload/proxy?url=${encodeURIComponent(url)}&download=true&filename=${encodeURIComponent(filename || 'file')}`;
  }
  return url;
};

const formatPageRange = (pages) => {
  if (!pages || !pages.length) return '';
  const sorted = [...pages].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) end = sorted[i];
    else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
};

// Client-side print helper (loads PDF into a hidden iframe and opens the system print dialog)
const printFileClientSide = async (url, name, specs) => {
  // Show a toast notifying the admin of the custom user specifications to set in the dialog
  toast(
    (t) => (
      <div>
        <strong>Print Settings for: {name}</strong>
        <div style={{ marginTop: 4, fontSize: '0.85em', color: '#4b5563' }}>
          Please configure in dialog: <span style={{ color: '#4f46e5', fontWeight: 'bold' }}>{specs}</span>
        </div>
      </div>
    ),
    { duration: 10000, icon: '🖨️' }
  );

  // Fetch the file through our proxy to bypass CORS sandbox restrictions
  const proxyUrl = getProxyUrl(url);
  const response = await api.get(proxyUrl, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);

  // Create a hidden iframe
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = blobUrl;

  document.body.appendChild(iframe);

  return new Promise((resolve) => {
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      // Keep iframe mounted long enough for native print dialog handover
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
        resolve();
      }, 1500);
    };
  });
};

/* ── Item renderers ── */
const PrintItem = ({ item }) => {
  const o = item.options || {};
  const specs = [
    o.copies > 1 ? `${o.copies} copies` : '1 copy',
    o.pages ? `${o.pages} page${o.pages !== 1 ? 's' : ''}` : null,
    o.pageSelection === 'range' && o.pageRange ? (typeof o.pageRange === 'string' ? `Pages ${o.pageRange}` : `Pages ${o.pageRange.start}-${o.pageRange.end}`) : null,
    o.pageSelection === 'custom' && o.customPages ? `Pages ${formatPageRange(o.customPages)}` : null,
    COLOR_LABEL[o.color] || o.color || null,
    SIDE_LABEL[o.sides] || o.sides || null,
    ORIENT_LABEL[o.orientation] || o.orientation || null,
    o.scale ? `Scale: ${o.scale}%` : null,
  ].filter(Boolean).join(' · ');

  const [printing, setPrinting] = useState(false);

  const handlePrint = async () => {
    if (!item.fileUrl) return;
    setPrinting(true);
    try {
      await printFileClientSide(item.fileUrl, item.name, specs);
      toast.success(`Print dialog opened: ${item.name}`);
    } catch (err) {
      toast.error('Failed to trigger print: ' + (err.message || err));
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="ao-item-card">
      <div className="ao-item-top">
        <span className="ao-badge print">PRINT</span>
        <span className="ao-item-name">{item.name}</span>
        <span className="ao-item-price">₹{item.price}</span>
      </div>
      {specs && <div className="ao-item-specs">{specs}</div>}
      {item.fileUrl && (
        <div className="ao-file-actions-wrapper">
          <div className="ao-file-actions">
            <a href={getProxyUrl(item.fileUrl)} target="_blank" rel="noopener noreferrer" className="ao-file-link preview">
              <FiEye size={12} /> Preview
            </a>
            <a href={getDownloadUrl(item.fileUrl, item.name)} className="ao-file-link download">
              <FiDownload size={12} /> Download
            </a>
          </div>
          <button className="btn-print-direct" onClick={handlePrint} disabled={printing}>
            <FiPrinter size={11} /> {printing ? 'Preparing...' : 'Print File'}
          </button>
        </div>
      )}
    </div>
  );
};

const PassportItem = ({ item }) => {
  const o = item.options || {};
  const specParts = [o.passportPack, o.passportSize, o.passportDims, o.passportBg ? `${o.passportBg} bg` : null];
  if (o.extraCopies > 0) specParts.push(`+${o.extraCopies * 8} extra`);
  const specs = specParts.filter(Boolean).join(' · ');

  return (
    <div className="ao-item-card">
      <div className="ao-item-top">
        <span className="ao-badge photo">PHOTO</span>
        <span className="ao-item-name">{item.name}</span>
        <span className="ao-item-price">₹{item.price}</span>
      </div>
      {specs && <div className="ao-item-specs">{specs}</div>}
      {item.fileUrl ? (
        <div className="ao-file-actions">
          <a href={getProxyUrl(item.fileUrl)} target="_blank" rel="noopener noreferrer" className="ao-file-link photo preview">
            📸 Preview Photo
          </a>
          <a href={getDownloadUrl(item.fileUrl, item.name || 'passport_photo')} className="ao-file-link photo download">
            <FiDownload size={12} /> Download Photo
          </a>
        </div>
      ) : (
        <div className="ao-no-photo">No photo uploaded — customer will bring original</div>
      )}
    </div>
  );
};

const MartItem = ({ item }) => (
  <div className="ao-item-card">
    <div className="ao-item-top">
      <span className="ao-badge mart">MART</span>
      <span className="ao-item-name">{item.name}</span>
      {item.quantity > 1 && <span className="ao-item-qty">×{item.quantity}</span>}
      <span className="ao-item-price">₹{item.price * item.quantity}</span>
    </div>
  </div>
);

const printPvcDocumentSlip = (item) => {
  const o = item.options || {};
  const lostDetails = o.lostCardDetails || {};
  const gov = o.govVerificationData || {};
  const govDetails = gov.details || {};
  const type = o.lostCardType || '';

  let cardTitle = 'AUTHENTIC PVC DOCUMENT SLIP';
  let issuer = gov.provider || 'Bharat Sarkar Digital Verification';
  let primaryId = '';
  let idLabel = 'Document ID';
  let holderName = '';
  let subDetails = [];
  let portalUrl = 'https://parivahan.gov.in/';

  if (type === 'lost_dl') {
    cardTitle = 'DRIVING LICENCE - UNION OF INDIA';
    issuer = gov.provider || 'Parivahan Sarathi (MoRTH)';
    idLabel = 'DL NO';
    primaryId = lostDetails.dlNumber || govDetails.dlNumber || '';
    holderName = lostDetails.dlHolderName || govDetails.holderName || '';
    subDetails = [
      { label: 'Date of Birth', value: lostDetails.dlDob || govDetails.dob || '—' },
      { label: 'Issuing RTO', value: govDetails.issuingRTO || 'Jhajjar RTO, Haryana' },
      { label: 'Vehicle Classes', value: Array.isArray(govDetails.vehicleClasses) ? govDetails.vehicleClasses.join(', ') : 'MCWG, LMV' },
      { label: 'Status', value: govDetails.status || 'ACTIVE' }
    ];
    portalUrl = 'https://sarathi.parivahan.gov.in/';
  } else if (type === 'lost_rc') {
    cardTitle = 'CERTIFICATE OF REGISTRATION';
    issuer = gov.provider || 'Parivahan Vahan (MoRTH)';
    idLabel = 'VEHICLE NO';
    primaryId = lostDetails.vehicleNumber || govDetails.vehicleNumber || '';
    holderName = lostDetails.rcOwnerName || govDetails.ownerName || '';
    subDetails = [
      { label: 'Chassis No (Last 5)', value: lostDetails.chassisLast5 || govDetails.chassisNumber || '—' },
      { label: 'Authority', value: govDetails.registeringAuthority || 'Jhajjar RTO, Haryana' },
      { label: 'Maker / Model', value: govDetails.makerModel || 'Verified On File' },
      { label: 'Status', value: govDetails.status || 'ACTIVE' }
    ];
    portalUrl = 'https://vahan.parivahan.gov.in/vahanservice/';
  } else if (type === 'lost_pan') {
    cardTitle = 'INCOME TAX DEPARTMENT - GOVT. OF INDIA';
    issuer = gov.provider || 'Income Tax Dept / NSDL';
    idLabel = 'PAN NO';
    primaryId = lostDetails.panNumber || govDetails.panNumber || '';
    holderName = lostDetails.panName || govDetails.fullName || '';
    subDetails = [
      { label: 'Date of Birth', value: lostDetails.panDob || govDetails.dob || '—' },
      { label: 'Category', value: govDetails.category || 'Individual' },
      { label: 'Aadhaar Seeded', value: govDetails.aadhaarSeeded || 'Yes' },
      { label: 'Status', value: govDetails.panStatus || govDetails.status || 'OPERATIVE' }
    ];
    portalUrl = 'https://www.onlineservices.nsdl.com/paam/ReprintEPan.html';
  } else if (type === 'lost_vote') {
    cardTitle = 'ELECTION COMMISSION OF INDIA';
    issuer = gov.provider || 'Election Commission of India';
    idLabel = 'EPIC NO';
    primaryId = lostDetails.epicNumber || govDetails.epicNumber || '';
    holderName = lostDetails.voterName || govDetails.name || '';
    subDetails = [
      { label: 'State / UT', value: lostDetails.voterState || govDetails.state || 'Haryana' },
      { label: 'Assembly Constituency', value: govDetails.assemblyConstituency || '66 - Jhajjar (SC)' },
      { label: 'Parliamentary', value: govDetails.parliamentaryConstituency || 'Rohtak' },
      { label: 'Status', value: 'VALID ELECTOR' }
    ];
    portalUrl = 'https://voters.eci.gov.in/';
  }

  const win = window.open('', '_blank');
  if (!win) {
    toast.error('Please allow popups to view document slip');
    return;
  }

  const subHtml = subDetails.map(function(s) {
    return '<tr><td class="label">' + s.label + '</td><td class="val">' + s.value + '</td></tr>';
  }).join('');

  const cardSubTop = subDetails.slice(0, 2).map(function(s) {
    return '<div><strong>' + s.label.toUpperCase() + ':</strong> ' + s.value + '</div>';
  }).join('');

  const cardSubBottom = subDetails.slice(2).map(function(s) {
    return '<div><strong>' + s.label.toUpperCase() + ':</strong> ' + s.value + '</div>';
  }).join('');

  const notesHtml = lostDetails.additionalNotes
    ? '<tr><td class="label">Notes</td><td class="val">' + lostDetails.additionalNotes + '</td></tr>'
    : '';

  const html = '<!DOCTYPE html>' +
'<html>' +
'<head>' +
'  <meta charset="utf-8">' +
'  <title>' + cardTitle + ' - ' + primaryId + '</title>' +
'  <style>' +
'    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 24px; background: #f8fafc; color: #0f172a; }' +
'    .action-bar { display: flex; gap: 12px; margin-bottom: 24px; }' +
'    .btn { padding: 10px 18px; border-radius: 8px; font-weight: 600; cursor: pointer; text-decoration: none; border: none; font-size: 14px; }' +
'    .btn-print { background: #4f46e5; color: #fff; }' +
'    .btn-portal { background: #0284c7; color: #fff; }' +
'    .btn-close { background: #e2e8f0; color: #334155; }' +
'    .card-container { display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 28px; }' +
'    .pvc-card { width: 85.6mm; height: 53.98mm; border: 2px solid #cbd5e1; border-radius: 3.5mm; padding: 4mm; box-sizing: border-box; background: #ffffff; position: relative; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }' +
'    .card-header { font-size: 8.5px; font-weight: 800; text-transform: uppercase; color: #1e3a8a; border-bottom: 1.5px solid #1e3a8a; padding-bottom: 1.5mm; margin-bottom: 2mm; text-align: center; }' +
'    .card-body { font-size: 8px; line-height: 1.4; color: #1e293b; }' +
'    .card-id-highlight { font-size: 11px; font-weight: 800; color: #0f172a; margin-top: 1mm; margin-bottom: 2mm; background: #f1f5f9; padding: 1mm 2mm; border-radius: 2mm; letter-spacing: 0.5px; }' +
'    .card-footer { position: absolute; bottom: 2.5mm; left: 4mm; right: 4mm; font-size: 6.5px; color: #64748b; text-align: center; border-top: 1px dashed #cbd5e1; padding-top: 1mm; }' +
'    .details-table { width: 100%; max-width: 600px; border-collapse: collapse; margin-top: 16px; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }' +
'    .details-table td { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }' +
'    .details-table td.label { font-weight: 600; color: #64748b; width: 35%; }' +
'    .details-table td.val { font-weight: 700; color: #0f172a; }' +
'    @media print {' +
'      body { background: #fff; margin: 0; padding: 0; }' +
'      .action-bar { display: none !important; }' +
'      .pvc-card { box-shadow: none; border-color: #000; page-break-inside: avoid; }' +
'    }' +
'  </style>' +
'</head>' +
'<body>' +
'  <div class="action-bar">' +
'    <button class="btn btn-print" onclick="window.print()">Print PVC Slip (CR-80)</button>' +
'    <a class="btn btn-portal" href="' + portalUrl + '" target="_blank" rel="noopener noreferrer">Open Official Portal</a>' +
'    <button class="btn btn-close" onclick="window.close()">Close</button>' +
'  </div>' +
'  <h2>Official PVC Card Document Slip</h2>' +
'  <p style="color: #64748b; font-size: 13px; margin-top: -6px;">Verified details formatted for CR-80 PVC Card reprint fulfillment.</p>' +
'  <div class="card-container">' +
'    <div class="pvc-card">' +
'      <div class="card-header">' + cardTitle + '</div>' +
'      <div class="card-body">' +
'        <div><strong>' + idLabel + ':</strong></div>' +
'        <div class="card-id-highlight">' + (primaryId || 'PENDING') + '</div>' +
'        <div><strong>NAME:</strong> ' + (holderName || 'REGISTERED CARDHOLDER') + '</div>' +
'        ' + cardSubTop +
'      </div>' +
'      <div class="card-footer">BHARAT SARKAR VERIFIED - SHUBHAM COMPUTERS CR-80 PRINT SPEC</div>' +
'    </div>' +
'    <div class="pvc-card">' +
'      <div class="card-header">CARD SPECIFICATIONS & VERIFICATION</div>' +
'      <div class="card-body">' +
'        <div><strong>ISSUING AUTHORITY:</strong> ' + issuer + '</div>' +
'        ' + cardSubBottom +
'        <div style="margin-top: 3mm; font-size: 7px; color: #475569;">' +
'          Standard ISO/IEC 7810 ID-1 PVC format (85.60 mm x 53.98 mm).' +
'        </div>' +
'      </div>' +
'      <div class="card-footer">OFFICIAL REPRINT SLIP - AUTHORIZED SYSTEM</div>' +
'    </div>' +
'  </div>' +
'  <h3>Customer Submitted & Verified Records</h3>' +
'  <table class="details-table">' +
'    <tr>' +
'      <td class="label">Service</td>' +
'      <td class="val">' + (item.name || 'PVC Card Service') + '</td>' +
'    </tr>' +
'    <tr>' +
'      <td class="label">' + idLabel + '</td>' +
'      <td class="val">' + (primaryId || '—') + '</td>' +
'    </tr>' +
'    <tr>' +
'      <td class="label">Cardholder Name</td>' +
'      <td class="val">' + (holderName || '—') + '</td>' +
'    </tr>' +
'    ' + subHtml +
'    ' + notesHtml +
'    <tr>' +
'      <td class="label">Official Portal</td>' +
'      <td class="val"><a href="' + portalUrl + '" target="_blank" style="color: #0284c7; text-decoration: underline;">' + portalUrl + '</a></td>' +
'    </tr>' +
'  </table>' +
'</body>' +
'</html>';

  win.document.write(html);
  win.document.close();
};

const PvcItem = ({ item }) => {
  const o = item.options || {};
  const gov = o.govVerificationData;
  const lostDetails = o.lostCardDetails || {};
  const lostType = o.lostCardType || (item.name?.toLowerCase().includes('dl') ? 'lost_dl' : item.name?.toLowerCase().includes('rc') ? 'lost_rc' : item.name?.toLowerCase().includes('pan') ? 'lost_pan' : item.name?.toLowerCase().includes('vote') ? 'lost_vote' : null);
  const isLostCard = Boolean(lostType || o.lostDescription || o.lostCardDetails);

  let portalUrl = 'https://parivahan.gov.in/';
  let primaryId = '';
  if (lostType === 'lost_dl') {
    portalUrl = 'https://sarathi.parivahan.gov.in/';
    primaryId = lostDetails.dlNumber || gov?.details?.dlNumber || '';
  } else if (lostType === 'lost_rc') {
    portalUrl = 'https://vahan.parivahan.gov.in/vahanservice/';
    primaryId = lostDetails.vehicleNumber || gov?.details?.vehicleNumber || '';
  } else if (lostType === 'lost_pan') {
    portalUrl = 'https://www.onlineservices.nsdl.com/paam/ReprintEPan.html';
    primaryId = lostDetails.panNumber || gov?.details?.panNumber || '';
  } else if (lostType === 'lost_vote') {
    portalUrl = 'https://voters.eci.gov.in/';
    primaryId = lostDetails.epicNumber || gov?.details?.epicNumber || '';
  }

  return (
    <div className="ao-item-card">
      <div className="ao-item-top">
        <span className="ao-badge pvc" style={{ background: '#7c3aed22', color: '#7c3aed' }}>PVC</span>
        <span className="ao-item-name">{item.name}</span>
        <span className="ao-item-price">₹{item.price * item.quantity}</span>
      </div>
      {o.lostDescription && (
        <div className="ao-item-specs" style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
          <strong>Lost details:</strong> {o.lostDescription}
        </div>
      )}
      {gov && (
        <div className="ao-gov-card" style={{ marginTop: 8, padding: 10, background: 'rgba(16, 185, 129, 0.08)', border: '1px solid #10b981', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#10b981', fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>
            <span>🏛️ Bharat Sarkar Verified Document</span>
            <span style={{ fontSize: '0.72rem', background: 'rgba(16, 185, 129, 0.2)', padding: '2px 8px', borderRadius: 4 }}>{gov.provider}</span>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-primary)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6 }}>
            {gov.details && Object.entries(gov.details).map(([k, v]) => (
              <div key={k} style={{ background: 'var(--bg-card)', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize', display: 'block', fontSize: '0.72rem' }}>
                  {k.replace(/([A-Z])/g, ' $1')}:
                </span>
                <strong style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                  {Array.isArray(v) ? v.join(', ') : String(v)}
                </strong>
              </div>
            ))}
          </div>
        </div>
      )}
      {isLostCard && (
        <div className="ao-pvc-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            className="ao-file-link preview"
            style={{ background: '#7c3aed', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
            onClick={() => printPvcDocumentSlip(item)}
          >
            <FiFileText size={13} /> Generate Document Slip
          </button>
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ao-file-link"
            style={{ background: 'var(--bg-card)', color: '#0284c7', border: '1px solid var(--border)', padding: '6px 12px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', fontSize: '0.8rem', fontWeight: 600 }}
          >
            <FiExternalLink size={13} /> Open Gov Portal
          </a>
          {primaryId && (
            <button
              type="button"
              className="ao-file-link"
              style={{ background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', padding: '6px 12px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8rem' }}
              onClick={() => {
                navigator.clipboard?.writeText(primaryId);
                toast.success(`Copied ID: ${primaryId}`);
              }}
            >
              <FiCopy size={13} /> Copy ID: {primaryId}
            </button>
          )}
        </div>
      )}
      {(o.frontFileUrl || o.backFileUrl) && (
        <div className="ao-file-actions" style={{ marginTop: 8 }}>
          {o.frontFileUrl && (
            <div style={{ display: 'flex', gap: 6, width: '100%' }}>
              <a href={getProxyUrl(o.frontFileUrl)} target="_blank" rel="noopener noreferrer" className="ao-file-link preview" style={{ flex: 1 }}>
                <FiEye size={12} /> Front Preview
              </a>
              <a href={getDownloadUrl(o.frontFileUrl, `${item.name}_front`)} className="ao-file-link download" style={{ flex: 1 }}>
                <FiDownload size={12} /> Front Download
              </a>
            </div>
          )}
          {o.backFileUrl && (
            <div style={{ display: 'flex', gap: 6, width: '100%', marginTop: 4 }}>
              <a href={getProxyUrl(o.backFileUrl)} target="_blank" rel="noopener noreferrer" className="ao-file-link preview" style={{ flex: 1 }}>
                <FiEye size={12} /> Back Preview
              </a>
              <a href={getDownloadUrl(o.backFileUrl, `${item.name}_back`)} className="ao-file-link download" style={{ flex: 1 }}>
                <FiDownload size={12} /> Back Download
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InquiryItem = ({ item }) => {
  const o = item.options || {};
  return (
    <div className="ao-item-card">
      <div className="ao-item-top">
        <span className="ao-badge inquiry">BULK</span>
        <span className="ao-item-name">Bulk / Custom Order</span>
        <span className="ao-item-price ao-quote-label">Quote pending</span>
      </div>
      {o.requirements && (
        <div className="ao-item-specs" style={{ whiteSpace: 'pre-wrap' }}>{o.requirements}</div>
      )}
      {o.quantity && <div className="ao-item-specs">Qty: {o.quantity}</div>}
    </div>
  );
};

const renderItem = (item, i) => {
  if (item.type === 'print')   return <PrintItem key={i} item={item} />;
  if (item.type === 'inquiry') return <InquiryItem key={i} item={item} />;
  if (item.type === 'mart' && item.options?.passportPack) return <PassportItem key={i} item={item} />;
  if (item.type === 'mart' && (item.options?.service || item.options?.frontFileUrl || item.options?.backFileUrl || item.options?.lostDescription)) return <PvcItem key={i} item={item} />;
  return <MartItem key={i} item={item} />;
};

/* ── Main Component ── */
const ManageOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [expanded, setExpanded] = useState({});

  // Printer configurations
  const [printingOrder, setPrintingOrder] = useState(null);
  const [printProgress, setPrintProgress] = useState({});

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get('/admin/orders');
      setOrders(data.orders || []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus.replace(/-/g, ' ')}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };



  const getNextStatus = (current) => {
    const idx = statusFlow.indexOf(current);
    return idx !== -1 && idx < statusFlow.length - 1 ? statusFlow[idx + 1] : null;
  };

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  // Direct Batch Printing Logic
  const handlePrintAll = async (order) => {
    const printItems = order.items.filter(it => it.type === 'print' && it.fileUrl);
    if (printItems.length === 0) return;

    setPrintingOrder(order._id);
    setPrintProgress(prev => ({
      ...prev,
      [order._id]: { current: 0, total: printItems.length }
    }));

    let successCount = 0;
    try {
      for (let i = 0; i < printItems.length; i++) {
        const item = printItems[i];
        setPrintProgress(prev => ({
          ...prev,
          [order._id]: { ...prev[order._id], current: i + 1 }
        }));

        const o = item.options || {};
        const specs = [
          o.copies > 1 ? `${o.copies} copies` : '1 copy',
          o.pages ? `${o.pages} page${o.pages !== 1 ? 's' : ''}` : null,
          o.pageSelection === 'range' && o.pageRange ? (typeof o.pageRange === 'string' ? `Pages ${o.pageRange}` : `Pages ${o.pageRange.start}-${o.pageRange.end}`) : null,
          o.pageSelection === 'custom' && o.customPages ? `Pages ${formatPageRange(o.customPages)}` : null,
          COLOR_LABEL[o.color] || o.color || null,
          SIDE_LABEL[o.sides] || o.sides || null,
          ORIENT_LABEL[o.orientation] || o.orientation || null,
          o.scale ? `Scale: ${o.scale}%` : null,
        ].filter(Boolean).join(' · ');

        try {
          await printFileClientSide(item.fileUrl, item.name, specs);
          successCount++;
        } catch (err) {
          console.error(`Failed to print file ${item.name}:`, err);
          toast.error(`Failed to print: ${item.name}`);
        }
        
        if (i < printItems.length - 1) {
          // Add a short gap between print dialog popups so they don't lock the UI
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      if (successCount === printItems.length) {
        toast.success(`All ${printItems.length} print dialogs opened!`);
      } else if (successCount > 0) {
        toast.success(`${successCount} of ${printItems.length} print dialogs opened.`);
      }
    } catch (err) {
      toast.error('An error occurred during batch printing.');
    } finally {
      setPrintingOrder(null);
    }
  };

  const filters = ['all', 'confirmed', 'delivered', 'cancelled'];
  const filteredOrders = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  if (loading) return <div className="page-container"><div className="loading-spinner" /></div>;

  return (
    <div className="page-container">
      <div className="page-top-bar">
        <button className="back-btn" onClick={() => navigate('/admin')}><FiArrowLeft /></button>
        <h1>Manage Orders</h1>
      </div>



      {/* Filter chips */}
      <div className="filter-bar">
        {filters.map(f => (
          <button key={f} className={`filter-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f.replace(/-/g, ' ')}
          </button>
        ))}
      </div>

      <div className="orders-count">{filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}</div>

      {filteredOrders.length === 0 ? (
        <div className="empty-state">No orders found</div>
      ) : (
        <div className="admin-orders-list">
          {filteredOrders.map(order => {
            const next  = getNextStatus(order.status);
            const color = statusColors[order.status] || '#888';
            const open  = expanded[order._id];
            const itemCount = order.items?.length || 0;
            const hasPrintFiles = order.items?.some(it => it.type === 'print' && it.fileUrl);

            return (
              <div className="admin-order-card" key={order._id}>

                {/* ── Row 1: Order ID + status ── */}
                <div className="ao-header">
                  <div className="ao-header-left">
                    <span className="ao-id">{order.orderId}</span>
                    <span className="ao-time-ago">{timeAgo(order.createdAt)}</span>
                  </div>
                  <span className="ao-status" style={{ background: color + '22', color }}>
                    {order.status === 'out-for-delivery' ? 'Out for delivery' : order.status}
                  </span>
                </div>

                {/* ── Row 2: Customer name + phone ── */}
                <div className="ao-customer">
                  <div className="ao-customer-info">
                    <span className="ao-customer-name">{order.customer?.name || 'Customer'}</span>
                    <span className="ao-user-id">UID: {String(order.customer?._id || '').slice(-10)}</span>
                  </div>
                  {order.customer?.phone && (
                    <a href={`tel:${order.customer.phone}`} className="ao-phone">
                      <FiPhone size={12} /> {order.customer.phone}
                    </a>
                  )}
                </div>

                {/* ── Row 3: Time placed + amount ── */}
                <div className="ao-meta-row">
                  <span className="ao-meta-time">🕐 {formatDateTime(order.createdAt)}</span>
                  <span className="ao-amount">₹{order.amount}</span>
                </div>

                {/* ── Row 4: Delivery address ── */}
                <div className="ao-address">
                  📍 {order.deliveryAddress?.full || order.deliveryAddress?.label || 'Address not available'}
                </div>

                {/* ── Items (expandable) ── */}
                <div className="ao-items-toggle" role="button" onClick={() => toggle(order._id)}>
                  <span className="ao-items-toggle-label">
                    {itemCount} item{itemCount !== 1 ? 's' : ''}
                    {!open && <span className="ao-items-types">
                      {order.items?.map((it, i) => (
                        <span key={i} className={`ao-badge-sm ${
                          it.type === 'print' ? 'print'
                          : it.type === 'inquiry' ? 'inquiry'
                          : it.options?.passportPack ? 'photo'
                          : 'mart'
                        }`}>
                          {it.type === 'print' ? 'PRINT' : it.type === 'inquiry' ? 'BULK' : it.options?.passportPack ? 'PHOTO' : 'MART'}
                        </span>
                      ))}
                    </span>}
                  </span>
                  {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </div>

                {open && (
                  <div className="ao-items-expanded">
                    {order.items?.map((item, i) => renderItem(item, i))}
                  </div>
                )}

                {/* Direct Batch Printing Console */}
                {open && hasPrintFiles && (
                  <div className="ao-batch-print-box">
                    <div className="batch-print-header">
                      <div className="batch-print-title">
                        <FiPrinter size={13} />
                        <span>Print Order Documents ({order.orderId})</span>
                      </div>
                      <button 
                        className="btn-print-all" 
                        onClick={() => handlePrintAll(order)} 
                        disabled={printingOrder === order._id}
                      >
                        <FiPrinter size={12} /> 
                        {printingOrder === order._id ? 'Printing...' : 'Print All Files'}
                      </button>
                    </div>
                    {printingOrder === order._id && printProgress[order._id] && (
                      <div className="batch-print-progress">
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${(printProgress[order._id].current / printProgress[order._id].total) * 100}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">
                          Printing file {printProgress[order._id].current} of {printProgress[order._id].total}...
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Admin note ── */}
                {order.adminNote && (
                  <div className="ao-admin-note">📌 {order.adminNote}</div>
                )}

                {/* ── Actions ── */}
                <div className="ao-footer">
                  <div className="ao-actions">
                    {order.status === 'confirmed' && (
                      <button className="ao-cancel-btn" onClick={() => updateStatus(order._id, 'cancelled')}>
                        Cancel
                      </button>
                    )}
                    {next && (
                      <button className="ao-next-btn" onClick={() => updateStatus(order._id, next)}>
                        Mark {next.replace(/-/g, ' ')}
                      </button>
                    )}
                    {(order.status === 'delivered' || order.status === 'cancelled') && (
                      <span className="ao-done-label" style={{ color }}>
                        {order.status === 'delivered' ? '✓ Completed' : '✗ Cancelled'}
                        {order.deliveredAt && ` · ${formatDateTime(order.deliveredAt)}`}
                      </span>
                    )}
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageOrders;
